import { useMemo, Dispatch, SetStateAction } from 'react'
import { ApolloError } from '@apollo/client'
import { message } from 'antd'
import { RefetchExceptionsVariablesType } from '@ProdWb/components/ExceptionFailuresDrawer/types'
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form'
import { MILESTONE_LIST as milestone } from '@ProdWb/JobList/constants'

import logger from '~/lib/logger'
import { getFileExtension } from '~/lib/getFileExtension'
import {
    useAddExceptionFailureNoteMutation,
    useMarkUploadedNoteAttachmentMutation,
    useCreateExceptionFailureMutation,
    useSetExceptionFailurePartsMutation,
    useUpdateExceptionFailureMutation,
    useUpdateExceptionFailurePartsMutation,
    useJobRequestPartsQuery,
    ExceptionFailureInput,
    JobListDocument,
    ExceptionFailuresDocument,
    JobRequestsQuery,
    ExceptionFailureUpdateInput,
    ExceptionFailureDocument,
    ExceptionFailureSource,
    SchedulingQueueDocument,
    JobDetailsDocument,
    AssignedEntityType,
    ExceptionFailureType,
    ExceptionFailureQuery,
    useCreateExceptionFailureNoteAttachmentMutation,
    useRemoveExceptionFailureNoteAttachmentMutation,
    ExceptionFailureDetailsDocument,
    JobBidsDocument,
    useGetExceptionTypesQuery,
    ExceptionType,
} from '~/gql/graphql'

import {
    NoteAttachmentType,
    FileAttachmentType,
    SelectedPartType,
    PartsWithFailedQty,
} from './types'
import {
    ERROR_DURATION,
    NOT_APPLICABLE,
    EXCEPTION_FORM_FIELDS,
} from './constants'

const TABLE_DATA_LIMIT = 10

type MutationOperationsType = ReturnType<typeof MutationOperations>

export type FailureModalMutationOperationsType = MutationOperationsType

export function MutationOperations() {
    const [createAttachment] = useCreateExceptionFailureNoteAttachmentMutation()
    const [removeExceptionNoteAttachment] =
        useRemoveExceptionFailureNoteAttachmentMutation()
    const [markUploadedAttachment] = useMarkUploadedNoteAttachmentMutation()
    const [createExceptionFailure] = useCreateExceptionFailureMutation()
    const [addNewNote] = useAddExceptionFailureNoteMutation()
    const [setParts] = useSetExceptionFailurePartsMutation()
    const [updateParts] = useUpdateExceptionFailurePartsMutation()
    const [updateException] = useUpdateExceptionFailureMutation()

    return {
        createAttachment: (file: FileAttachmentType) => {
            const variables = {
                input: {
                    fileName: file.name,
                    extension: getFileExtension(file.name),
                    mimeType: file.type,
                    type: null,
                },
            }

            return createAttachment({
                variables,
            })
                .then(res => res?.data?.createExceptionFailureNoteAttachment)
                .then(uploadData => {
                    if (!uploadData) {
                        return null
                    }

                    file.uploadUrl = uploadData?.uploadUrl || ''
                    file.id = uploadData?.id
                    file.attachmentId = uploadData?.id
                    return file
                })
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to create exception note attachment',
                            variables,
                        },
                    })
                })
        },
        useMarkUploadAttachment: (file: FileAttachmentType) => {
            const variables = {
                id: file.attachmentId,
            }

            return markUploadedAttachment({
                variables,
            })
                .then(res => res?.data?.markUploadedAsset)
                .then(markedData => {
                    file.attachmentId = markedData?.id
                    file.url = markedData?.downloadUrl || ''

                    return file
                })
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to markUpload attachment',
                            variables,
                        },
                    })
                })
        },
        removeNoteAttachment: async (id: string) => {
            const variables = {
                id,
            }

            try {
                const response = await removeExceptionNoteAttachment({
                    variables,
                })

                return response?.data?.removeExceptionFailureNoteAttachment
            } catch (err) {
                logger.exception(err, {
                    extra: {
                        info: 'Failed to remove attachment for exceptionFailure note',
                    },
                })
                message.error('Failed to remove attachment')
            }
        },
        createExceptionMutation: (
            input: ExceptionFailureInput,
            jobName: string,
            refetchExceptionsVariables?: RefetchExceptionsVariablesType,
            isForJobBoard?: boolean,
        ) => {
            const variables = { exceptionFailureInput: input }

            const refetchQueries = [
                {
                    query: JobDetailsDocument,
                    variables: {
                        id: input.jobId,
                    },
                },
                {
                    query: JobListDocument,
                    variables: {
                        limit: TABLE_DATA_LIMIT,
                        filters: { jobName },
                    },
                },
                {
                    query: ExceptionFailuresDocument,
                    variables: {
                        filters: refetchExceptionsVariables?.filters,
                        limit: refetchExceptionsVariables?.limit,
                    },
                },
                {
                    query: JobBidsDocument,
                    variables: {
                        jobId: input.jobId,
                    },
                },
            ]

            if (!isForJobBoard) {
                refetchQueries.push({
                    query: SchedulingQueueDocument,
                    variables: {
                        jobId: input.jobId,
                    },
                })
            }

            return createExceptionFailure({
                variables,
                refetchQueries,
            })
                .then(res => {
                    return {
                        data: res?.data?.createExceptionFailure,
                        error: '',
                    }
                })
                .catch(err => {
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to create exception',
                            variables,
                        },
                    })
                    return {
                        data: null,
                        error: err.message,
                    }
                })
        },
        addExceptionFailureNote: (
            exceptionId: string,
            body: string,
            attachmentIds: string[],
            refetchExceptionsVariables?: RefetchExceptionsVariablesType,
        ) => {
            const variables = {
                input: {
                    exceptionId,
                    body,
                    attachments: attachmentIds,
                    source: ExceptionFailureSource.FICTIV,
                },
            }

            return addNewNote({
                variables,
                refetchQueries: [
                    {
                        query: ExceptionFailuresDocument,
                        variables: {
                            filters: refetchExceptionsVariables?.filters,
                            limit: refetchExceptionsVariables?.limit,
                        },
                    },
                    {
                        query: ExceptionFailureDetailsDocument,
                        variables: { id: exceptionId },
                    },
                ],
            })
                .then(res => res?.data?.addExceptionFailureNote)
                .catch(err => {
                    message.error(
                        'Failed to add exception note',
                        ERROR_DURATION,
                    )
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to add exception note',
                            variables,
                        },
                    })
                })
        },
        setExceptionFailureParts: ({
            exceptionId,
            products,
        }: {
            exceptionId: string
            products: SelectedPartType[]
        }) => {
            const variables = {
                input: {
                    exceptionId,
                    products,
                },
            }

            return setParts({
                variables,
            })
                .then(res => res?.data?.setExceptionFailureParts)
                .catch(err => {
                    message.error(
                        'Failed to set exception parts',
                        ERROR_DURATION,
                    )
                    logger.exception(err, {
                        extra: {
                            info: 'Failed to set exception parts',
                            variables,
                        },
                    })
                })
        },
        updateExceptionFailureParts: ({
            exceptionId,
            products,
        }: {
            exceptionId: string
            products: SelectedPartType[]
        }) => {
            const variables = {
                input: {
                    exceptionId,
                    products,
                },
            }

            return updateParts({
                variables,
                refetchQueries: [
                    {
                        query: ExceptionFailureDetailsDocument,
                        variables: {
                            id: exceptionId,
                        },
                    },
                ],
            })
                .then(res => res.data?.updateExceptionFailureParts)
                .catch(err => {
                    message.error(
                        `Failed! ${(err as ApolloError).message}`,
                        ERROR_DURATION,
                    )
                    logger.exception(err as ApolloError, {
                        extra: {
                            info: 'Failed to update exception parts',
                            variables,
                        },
                    })
                })
        },
        updateException: (
            exceptionId: string,
            input: ExceptionFailureUpdateInput,
        ) => {
            const variables = {
                id: exceptionId,
                exceptionFailureInput: input,
            }

            return updateException({
                variables,
                refetchQueries: [
                    {
                        query: ExceptionFailureDocument,
                        variables: { id: exceptionId },
                    },
                    {
                        query: ExceptionFailureDetailsDocument,
                        variables: {
                            id: exceptionId,
                        },
                    },
                ],
            })
                .then(res => {
                    return {
                        data: res.data?.updateExceptionFailure,
                        error: null,
                    }
                })
                .catch(err => {
                    logger.exception(err as ApolloError, {
                        extra: {
                            info: 'Failed to update exception',
                            variables,
                        },
                    })
                    return {
                        data: null,
                        error: err.message,
                    }
                })
        },
    }
}

export const useExceptionSource = (
    dataList: ExceptionType[] | undefined | null,
) => {
    return useMemo(() => {
        return dataList?.map(object => ({
            value: object.id,
            label: object.name,
        }))
    }, [dataList])
}

export const useExceptionType = () => {
    const { data } = useGetExceptionTypesQuery()

    return useExceptionSource(data?.exceptionTypes as ExceptionType[])
}

export const createAttachment = (
    file: File,
    confirmFileUpload: (param: string) => void,
    mutationHandler: MutationOperationsType,
) => {
    confirmFileUpload('inprogress')
    return mutationHandler.createAttachment(file)
}

export const handleExceptionAttachmentUpload = async (
    file: File,
    setNoteAttachment: Dispatch<SetStateAction<NoteAttachmentType[]>>,
    confirmFileUpload: (param: string) => void,
    mutationHandler: MutationOperationsType,
) => {
    const fileAttachment = await mutationHandler.useMarkUploadAttachment(file)

    if (!fileAttachment) {
        message.error('Failed to upload attachment')
        return null
    }

    setNoteAttachment(oldArray => [
        ...oldArray,
        {
            id: fileAttachment.attachmentId,
            url: fileAttachment.url,
            fileName: file.name,
        },
    ])

    message.success('Attachment uploaded successfully')
    confirmFileUpload('success')
    return fileAttachment
}

export const removeExceptionNoteAttachment = async (
    setNoteAttachment: Dispatch<SetStateAction<NoteAttachmentType[]>>,
    noteAttachment: NoteAttachmentType[],
    mutationHandler: MutationOperationsType,
    id?: string,
) => {
    const response = id && (await mutationHandler.removeNoteAttachment(id))

    if (response) {
        setNoteAttachment(
            noteAttachment.filter(attachment => attachment.id !== id),
        )
    }
}

export const updateException = async (
    exceptionFailureUpdateInput: ExceptionFailureUpdateInput,
    note: string | null,
    noteAttachments: NoteAttachmentType[],
    exceptionId: string,
    selectedParts: SelectedPartType[],
    mutationHandler: MutationOperationsType,
) => {
    try {
        const response = await mutationHandler.updateException(
            exceptionId,
            exceptionFailureUpdateInput,
        )

        if (response.data) {
            if (selectedParts?.length > 0) {
                try {
                    await mutationHandler.updateExceptionFailureParts({
                        exceptionId: response.data.id,
                        products: selectedParts,
                    })
                } catch (err) {
                    message.error(
                        'Update selected parts failed',
                        ERROR_DURATION,
                    )
                }
            }

            if (note || noteAttachments.length) {
                try {
                    const attachmentsIds: string[] = []

                    noteAttachments?.map(item => {
                        if (item.id) {
                            attachmentsIds.push(item.id)
                        }
                    })

                    await mutationHandler.addExceptionFailureNote(
                        response.data.id,
                        note || NOT_APPLICABLE,
                        attachmentsIds,
                    )
                } catch (err) {
                    message.error('Add note failed', ERROR_DURATION)
                }
            }
            message.success('Exception updated successfully', ERROR_DURATION)
            return { exceptionId: response.data.id, error: null }
        } else {
            message.error(response.error)
            return { exceptionId: null, error: response.error }
        }
    } catch (err) {
        return { exceptionId: null, error: 'Failed to update exception' }
    }
}

export const createException = async (
    exceptionFailureInput: ExceptionFailureInput,
    selectedParts: SelectedPartType[],
    jobName: string,
    note: string,
    noteAttachments: NoteAttachmentType[],
    mutationHandler: MutationOperationsType,
    refetchExceptionsVariables?: RefetchExceptionsVariablesType,
    isForJobBoard?: boolean,
) => {
    try {
        const response = await mutationHandler.createExceptionMutation(
            exceptionFailureInput,
            jobName,
            refetchExceptionsVariables,
            isForJobBoard,
        )

        if (response.data) {
            if (selectedParts && selectedParts.length > 0) {
                try {
                    await mutationHandler.setExceptionFailureParts({
                        exceptionId: response.data.id,
                        products: selectedParts,
                    })
                } catch (err) {
                    message.error('Set selected parts failed', ERROR_DURATION)
                }
            }
            if (note || noteAttachments.length) {
                try {
                    const attachmentsIds: string[] = []

                    noteAttachments?.map(item => {
                        if (item.id) {
                            attachmentsIds.push(item.id)
                        }
                    })
                    await mutationHandler.addExceptionFailureNote(
                        response.data.id,
                        note || NOT_APPLICABLE,
                        attachmentsIds,
                        refetchExceptionsVariables,
                    )
                } catch (err) {
                    message.error('Add note failed', ERROR_DURATION)
                }
            }
            message.success('Exception created successfully', ERROR_DURATION)
            return { exceptionId: response.data.id, error: null }
        } else {
            message.error(response.error)
            return { exceptionId: null, error: response.error }
        }
    } catch (err) {
        message.error('Failed to create exception', ERROR_DURATION)
        return
    }
}

export const handleExceptionAttachmentCreation = (
    file: File,
    confirmFileUpload: (param: string) => void,
    mutationHandler: MutationOperationsType,
) => {
    confirmFileUpload('inprogress')
    return mutationHandler.createAttachment(file)
}

export const useJobRequestParts = (jobRequestId: number | null) => {
    const { data: partsData, loading: partsLoading } = useJobRequestPartsQuery({
        variables: { jobRequestId: jobRequestId as number },
        skip: !jobRequestId,
    })

    return {
        data: partsData?.getJobRequestParts || [],
        loading: partsLoading,
    }
}

export const disableResponsibility = (
    type: { value: string; label: string },
    form: WrappedFormUtils,
) => {
    if (
        form.getFieldValue(EXCEPTION_FORM_FIELDS.TYPE) ===
        ExceptionFailureType.IF
    ) {
        return false
    }

    if (
        (type.value === AssignedEntityType.MP &&
            milestone.scheduling ===
                form.getFieldValue(EXCEPTION_FORM_FIELDS.MILESTONE)) ||
        form.getFieldValue(EXCEPTION_FORM_FIELDS.MILESTONE) ===
            milestone.customer_delivery
    ) {
        return true
    }

    return false
}

export const shouldHideJobRequestSelect = (
    form: WrappedFormUtils,
    jobRequests: JobRequestsQuery['jobRequests'],
) => {
    if (
        (jobRequests && jobRequests?.length === 0) ||
        form.getFieldValue(EXCEPTION_FORM_FIELDS.MILESTONE) ===
            milestone.scheduling
    ) {
        return true
    }

    return false
}

export const isIFExceptionOnJob = (
    radioValue: string,
    jobRequests: JobRequestsQuery['jobRequests'],
    setRestrictExceptionCreation: Dispatch<SetStateAction<boolean>>,
) => {
    const shouldRestrict =
        radioValue === ExceptionFailureType.IF &&
        jobRequests &&
        jobRequests.length === 0

    setRestrictExceptionCreation(shouldRestrict || false)
    return shouldRestrict
}

export const shouldHideAffectedLines = (form: WrappedFormUtils) => {
    if (
        (!form.getFieldValue(EXCEPTION_FORM_FIELDS.JOB_REQUEST) &&
            form.getFieldValue(EXCEPTION_FORM_FIELDS.TYPE) !==
                ExceptionFailureType.RFI) ||
        form.getFieldValue(EXCEPTION_FORM_FIELDS.MILESTONE) ===
            milestone.scheduling
    ) {
        return true
    } else {
        return false
    }
}

export const shouldRestrictRFISelectiion = (
    form: WrappedFormUtils,
    exception?: ExceptionFailureQuery['exceptionFailure'],
) => {
    const shouldRestrict =
        exception?.isFailure &&
        (form.getFieldValue(EXCEPTION_FORM_FIELDS.TYPE) ===
            ExceptionFailureType.OT ||
            form.getFieldValue(EXCEPTION_FORM_FIELDS.TYPE) ===
                ExceptionFailureType.IF)

    return shouldRestrict
}

export const validateParts = (partsWithQty: PartsWithFailedQty[]) => {
    let isValid = true

    partsWithQty?.forEach(part => {
        if (
            part.isChecked &&
            (part.failedQuantity < 1 || part.orderedQty < part.failedQuantity)
        ) {
            isValid = false
        }
    })

    return isValid
}
